import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { History, Loader2, Download, User, TrendingUp, Wallet, RotateCcw } from "lucide-react";

/**
 * DossierBilanHistory · Shows the last 10 unified-PDF generations for the
 * current dossier with metadata (date, IMES, bankability, signer) + a
 * one-click "re-download" of the exact historical rendering.
 *
 * Data source:  GET /api/reports/dossier/{id}/bilan-history
 * Replay:       GET /api/reports/bilan-history/{history_id}/pdf
 */
export function DossierBilanHistory({ dossier }) {
  const dossierId = dossier?.dossier_id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!dossierId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/dossier/${dossierId}/bilan-history`);
      setItems(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Historique indisponible.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [dossierId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (h) => {
    // Auth-required — fetch as blob then trigger download so we keep the
    // Authorization header (unlike a plain window.location.href).
    try {
      const resp = await api.get(`/reports/bilan-history/${h.history_id}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `altiora-bilan-${h.dossier_id || dossierId}-${h.history_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Bilan historique téléchargé.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Téléchargement impossible.");
    }
  };

  return (
    <section id="dossier-bilan-history" className="mt-10" data-testid="dossier-bilan-history">
      <div className="rounded-2xl border border-[#E4E1D8] bg-white overflow-hidden">
        <div className="flex items-center justify-between border-b border-[#E4E1D8] px-6 py-4 bg-[#FBF6EA]/30">
          <div className="flex items-center gap-3">
            <History className="h-4 w-4 text-[#7A5B14]" strokeWidth={1.8} />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
                Historique des Bilans
              </div>
              <div className="font-display text-lg text-[#0B2545] tracking-tight">
                10 derniers PDF générés · rendus reproductibles
              </div>
            </div>
          </div>
          <button
            onClick={load}
            data-testid="bilan-history-refresh"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] hover:border-[#0B2545] px-3 py-1.5 text-xs text-[#5B5B62] hover:text-[#0B2545]"
          >
            <RotateCcw className="h-3 w-3" strokeWidth={2} /> Rafraîchir
          </button>
        </div>

        {loading && (
          <div className="px-6 py-8 flex items-center gap-2 text-sm text-[#5B5B62]">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> Chargement de l'historique…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="px-6 py-10 text-center">
            <p className="text-sm text-[#5B5B62]">
              Aucun Bilan encore généré. Cliquez sur <span className="font-semibold text-[#0B2545]">Bilan PDF</span> ci-dessus
              pour créer votre premier rendu — il sera archivé automatiquement.
            </p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <ul className="divide-y divide-[#E4E1D8]" data-testid="bilan-history-list">
            {items.map((h, idx) => (
              <li key={h.history_id}
                  className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 px-6 py-4 hover:bg-[#F8F7F3] transition-colors"
                  data-testid={`bilan-history-row-${h.history_id}`}>
                <div className="w-8 flex-shrink-0 text-center font-mono text-[10px] uppercase tracking-widest text-[#8B8B8F]">
                  #{String(idx + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1F2937]">
                    <span className="font-medium">{formatDate(h.generated_at)}</span>
                    <span className="ml-2 text-xs text-[#5B5B62]">
                      · {(h.size_bytes / 1024).toFixed(0)} Ko
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] font-mono text-[#5B5B62]">
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3 w-3" strokeWidth={2} />
                      {h.generated_by_name || "—"}
                      {h.signer_title && <span className="opacity-60"> · {h.signer_title}</span>}
                    </span>
                    {h.input_hash && (
                      <span className="opacity-60">empreinte {String(h.input_hash).slice(0, 8)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <MetricPill icon={TrendingUp} label="IMES" value={h.imes_score}
                    grade={h.imes_grade} />
                  <MetricPill icon={Wallet} label="Bank" value={h.bank_index}
                    grade={h.bank_grade} />
                  <button
                    onClick={() => download(h)}
                    data-testid={`bilan-history-download-${h.history_id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#0B2545] hover:bg-[#0B2545] text-[#0B2545] hover:text-white px-3 py-1.5 text-xs font-medium transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" strokeWidth={2} /> Ce rendu
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}


function MetricPill({ icon: Icon, label, value, grade }) {
  const show = value != null;
  return (
    <div className="flex items-center gap-1.5 min-w-[70px]">
      <Icon className="h-3 w-3 text-[#7A5B14]" strokeWidth={2} />
      <div className="text-right">
        <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B8B8F]">{label}</div>
        <div className="font-mono text-sm text-[#0B2545] tabular-nums leading-none">
          {show ? Number(value).toFixed(1) : "—"}
          {grade && <span className="ml-0.5 text-[9px] opacity-70">{grade}</span>}
        </div>
      </div>
    </div>
  );
}


function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}


export default DossierBilanHistory;
